import { FC, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { DatePicker, Form, Radio } from 'antd';
import { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { useIwEditingStore } from '@/entities/interwills';
import { Input } from '@/shared/components';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { AssetDto } from '@/shared/lib/sj-orm/models/asset.dto';
import { DtoId } from '@/shared/lib/sj-orm/models/base.dto';
import { PrivateDocumentDto } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { Icon } from '@/shared/uikit/Icon';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { SelectProofOfOwnershipModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';
type ProofOfOwnershipParams = {
  documentNumber: string;
  issueDate: Dayjs;
  selectedDocumentId: DtoId;
};

export const SelectProofOfOwnershipModal: FC<SelectProofOfOwnershipModalProps> = ({
  assetId,
  editId,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'proofOfOwnership' });

  const [tab, setTab] = useState(editId ? 1 : 0);

  const { updateAssets, assets } = useIwEditingStore();

  const modals = useModalsStore();
  const sjDatabase = useSJDatabase();

  const documents = sjDatabase.collections.privateDocuments?.findMany(() => true);

  const {
    control,
    handleSubmit: formHandleSubmit,
    watch,
    formState: { isSubmitting },
    setValue,
  } = useForm<ProofOfOwnershipParams>();

  const selectedDocumentId = editId || watch('selectedDocumentId');
  const selectedDocument = useMemo(() => {
    return documents?.find((item) => item.id === selectedDocumentId);
  }, [documents, selectedDocumentId]);

  useEffect(() => {
    if (selectedDocument) {
      setValue('documentNumber', (selectedDocument as unknown as { number: string })?.number);
      setValue(
        'issueDate',
        dayjs((selectedDocument as unknown as { startDate: string })?.startDate),
      );
    }
  }, [selectedDocument]);

  const onFinishHandler = async (values: ProofOfOwnershipParams) => {
    if (tab === 0) {
      setTab(1);

      return;
    }

    const document = await sjDatabase.collections.privateDocuments?.update({
      ...selectedDocument,
      startDate: values.issueDate.toISOString(),
      number: values.documentNumber,
    } as PrivateDocumentDto);

    const newAssets = [...assets];
    const idx = newAssets.findIndex((item) => item.id === assetId);
    if (document) {
      await sjDatabase.collections.assets?.update({
        ...newAssets[idx],
        id: newAssets[idx].id,
        category: newAssets[idx].category,
        otherDocuments: [...(newAssets[idx].otherDocuments ?? []), document.id],
      } as AssetDto & { otherDocuments: DtoId[] });

      newAssets[idx].proofOfOwnership = document;
      updateAssets(newAssets);
    }
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={selectedDocument ? selectedDocument.name : t('title')}>
      <InterwillStyledForm
        style={{
          gap: 24,
        }}
        onFinish={formHandleSubmit(onFinishHandler)}
      >
        {tab === 0 && (
          <>
            <Controller
              name={'selectedDocumentId'}
              control={control}
              rules={{ required: true }}
              render={({ field }) => {
                return (
                  <Radio.Group
                    {...field}
                    style={{
                      flexDirection: 'column',
                      display: 'flex',
                      gap: 12,
                      overflow: 'auto',
                    }}
                    options={documents?.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))}
                  />
                );
              }}
            />
            <ButtonIwText
              onClick={() => {
                modals.closeModal();
                modals.openModal({
                  type: 'ProofOfOwnershipModal',
                  assetId,
                });
              }}
              style={{
                width: 'fit-content',
              }}
              icon={<Icon name="plus" size="s20" />}
            >
              {t('addNewBtn')}
            </ButtonIwText>
          </>
        )}

        {tab === 1 && (
          <>
            <Controller
              name={'documentNumber'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <Input placeholder={t(`${field.name}.placeholder`)} {...field} />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
            <Controller
              name={'issueDate'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <DatePicker
                        style={{
                          width: '100%',
                          height: 40,
                        }}
                        placeholder={t(`${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          </>
        )}

        <Buttons>
          <ButtonIw htmlType="submit" loading={isSubmitting} disabled={isSubmitting}>
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 32px;
  width: 100%;
`;
